import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KeyBadge } from '@/components/ui/cifras/key-badge';
import {
  EmptyState,
  FilterChip,
  InfiniteScrollSentinel,
  ListSearch,
  ListSummary,
} from '@/components/ui/cifras/list-toolbar';
import {
  IconTile,
  ResourceCard,
  ResourceCardGrid,
} from '@/components/ui/cifras/resource-card';
import { Content } from '@/components/ui/content';
import { ActionsButton } from '@/components/ui/data-table';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { date } from '@/lib/date';
import { meta } from '@/lib/meta';
import { chordsOf } from '@/lib/song';
import { usePermissions } from '@/services/auth';
import { songKeys, useDeleteSong, useInfiniteSongs } from '@/services/songs';

export const Route = createFileRoute('/_app/songs/')({
  head: () => meta({ label: 'Cifras' }),
  component: RouteComponent,
});

const model = 'songs';
const PER_PAGE = 24;
const ROOTS = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'];

function RouteComponent() {
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { canCreate, canUpdate, canDelete } = createPermissions(model);
  const { mutateAsync: remove } = useDeleteSong();

  const [search, setSearch] = useState('');
  const [rootKey, setRootKey] = useState<string | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteSongs({
    search: search || undefined,
    rootKey: rootKey ?? undefined,
    orderBy: 'title',
    order: 'asc',
    perPage: PER_PAGE,
  });

  const songs = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.pagination.total ?? 0;

  return (
    <Content
      breadcrumbs={{
        icon: 'music',
        title: 'Cifras',
        action: canCreate && (
          <Button variant="success" onClick={() => push('/songs/create')} icon="plus">
            Nova cifra
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <ListSearch
            placeholder="Buscar por música ou artista..."
            onSearch={(term) => {
              setSearch(term);
            }}
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            <FilterChip
              active={rootKey === null}
              onClick={() => {
                setRootKey(null);
              }}
            >
              Todos os tons
            </FilterChip>
            {ROOTS.map((root) => (
              <FilterChip
                key={root}
                active={rootKey === root}
                onClick={() => {
                  setRootKey(rootKey === root ? null : root);
                }}
              >
                {root}
              </FilterChip>
            ))}
          </div>
        </div>

        {data && <ListSummary shown={songs.length} total={total} noun="cifras" />}

        {data && songs.length === 0 ? (
          <EmptyState
            title="Nenhuma cifra encontrada"
            hint={search || rootKey ? 'Tente outro termo ou tom.' : undefined}
          />
        ) : (
          <ResourceCardGrid>
            {songs.map((song) => (
              <ResourceCard
                key={song.id}
                muted={song.isActive === false}
                leading={
                  <IconTile>
                    <Icon name="music" className="size-[18px]" />
                  </IconTile>
                }
                title={song.title}
                subtitle={song.artist?.name ?? 'Sem artista'}
                trailing={
                  <div className="flex items-center gap-2">
                    {song.isActive === false && (
                      <Badge variant="destructive">Inativa</Badge>
                    )}
                    <KeyBadge value={song.originalKey} />
                  </div>
                }
                meta={
                  <>
                    {song.capo ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="guitar" className="size-3.5 text-accent-ink" />
                        Capo {song.capo}ª casa
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="list-music" className="size-3.5 text-accent-ink" />
                      {song.setlistCount}{' '}
                      {song.setlistCount === 1 ? 'repertório' : 'repertórios'}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="clock" className="size-3.5 text-accent-ink" />
                      {date(song.updatedAt as unknown as string).format('DD/MM/YYYY')}
                    </span>
                    <span className="flex w-full flex-wrap gap-1">
                      {chordsOf(song.content)
                        .slice(0, 6)
                        .map((chord) => (
                          <span
                            key={chord}
                            className="rounded-md bg-raised-2 px-1.5 py-0.5 font-bold text-[11px] text-accent-soft"
                          >
                            {chord}
                          </span>
                        ))}
                    </span>
                  </>
                }
                actions={
                  <>
                    <Button
                      size="sm"
                      variant="sand"
                      onClick={() => push(`/songs/${song.id}/view`)}
                    >
                      <Icon name="play" />
                      Palco
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      aria-label="Imprimir"
                      onClick={() => push(`/songs/${song.id}/print`)}
                    >
                      <Icon name="printer" />
                    </Button>
                    <ActionsButton
                      id={song.id}
                      edit={canUpdate}
                      remove={canDelete}
                      onEdit={(id) => push(`/songs/${id}`)}
                      onRemove={(id) => remove({ id })}
                      queryKey={songKeys.all}
                    />
                  </>
                }
                onClick={() => push(`/songs/${song.id}/view`)}
              />
            ))}
          </ResourceCardGrid>
        )}

        <InfiniteScrollSentinel
          hasNextPage={Boolean(hasNextPage)}
          isFetching={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </div>
    </Content>
  );
}
